import { useFormik } from "formik";
import moment from "moment";
import type { FC, HTMLAttributes } from "react";
import { useState } from "react";
import * as Yup from "yup";

import Icon from "@ch/components/Icons/Icon";
import Checkbox from "@ch/components/Inputs/Checkbox";
import DateTimePicker from "@ch/components/Inputs/DateTimePicker";
import InputSelect from "@ch/components/Inputs/InputSelect";
import NumberInput from "@ch/components/Inputs/NumberInput.tsx";
import TextInput from "@ch/components/Inputs/TextInput.tsx";
import Modal from "@ch/components/Modals/Modal";
import { CustomSelect } from "@ch/components/Select/Select";
import type {
  Frequency,
  Medication,
  NewMedicationPassport,
  NewPrescription,
  Prescription,
  PrescriptionWithRelations,
} from "@ch/generated";
import {
  dosageUnitOptions,
  sourceOptionsPrescriptions,
} from "@ch/helpers/utils/globalConstants";
import { useAppSelector } from "@ch/redux-store/hooks/useAppSelector";
import type { FormikState } from "formik/dist/types";
import useCreatePrescriptionPassport from "../hooks/prescription/useCreatePrescriptionPassport.ts";
import useGetPrescriptionById from "../hooks/prescription/useGetPrescriptionById";
import useUpdatePrescriptionPassport from "../hooks/prescription/useUpdatePrescriptionPassport";

export interface PrescriptionPayload {
  prescription?: NewPrescription;
  medicationPassports?: (NewMedicationPassport & { id?: string })[];
}

interface IFormValues {
  title: string;
  medicationId: string;
  frequency: string;
  startDate: Date;
  source: string;
  type: string;
  dosage: string;
  duration: number;
  dosagesUnit: string;
}

interface PrescriptionModalProps {
  modalId: string;
  onClose: () => void;
  userId?: string;
  frequencies: Frequency[];
  medications: Medication[];
  isOpen: boolean;
  unmountDialogue: () => void;
  prescription?: PrescriptionWithRelations;
  editMedicationPassportId?: string;
  editExistingMedication?: boolean;
  refetchSummary?: () => void;
  addSaveAndAdd?: boolean;
  refetchPrescription?: () => void;
}

const AddMedication: FC<PrescriptionModalProps> = ({
  modalId,
  onClose,
  userId,
  frequencies,
  medications,
  isOpen,
  addSaveAndAdd = true,
  unmountDialogue,
  prescription,
  editMedicationPassportId,
  editExistingMedication = false,
  refetchSummary,
  refetchPrescription,
}) => {
  const user = useAppSelector((state) => state.user.currentUser);
  const [saveAndAdd, setSaveAndAdd] = useState<boolean>(false);
  const [createdPrescriptionsPassportId, setCreatedPrescriptionsPassportId] =
    useState<string>("");

  const { prescriptionsPassport } = useGetPrescriptionById(
    createdPrescriptionsPassportId,
    Boolean(createdPrescriptionsPassportId),
  );
  const { updatePrescriptionPassport } = useUpdatePrescriptionPassport({
    id: createdPrescriptionsPassportId,
    callback: () => {
      if (!saveAndAdd) onClose();
      if (refetchPrescription) {
        refetchPrescription();
      }
    },
  });
  const medicationToEdit = prescription?.medicationPassports?.find(
    (item) => item?.id === editMedicationPassportId,
  );
  const { createPrescriptionPassport, loading } = useCreatePrescriptionPassport(
    {
      callback: (data: Prescription) => {
        if (saveAndAdd) {
          refetchSummary?.();
          setCreatedPrescriptionsPassportId(data.id!);
        } else {
          onClose();
        }
      },
    },
  );

  const preparePayload = (values: IFormValues) => {
    return {
      prescription: {
        userId: user?.id,
        title: String(values?.title),
      },
      medicationPassports: [
        {
          userId,
          medicationId: values.medicationId,
          type: values?.type ? (values?.type as "past" | "present") : "present",
          frequency: values.frequency,
          startDate: moment(values.startDate).toISOString(),
          source: values.source,
          dosagesUnit: String(values?.dosagesUnit),
          dosage: String(values?.dosage),
          durationInDays: Number(values?.duration),
        },
      ],
    };
  };

  const getResetFormObject = (formValues: IFormValues) => ({
    values: {
      title: formValues?.title,
      medicationId: "",
      frequency: "",
      startDate: new Date(),
      prescribedBy: "",
      type: "",
      dosage: "",
      duration: "",
      dosagesUnit: dosageUnitOptions[0].value,
    },
  });

  const onAddPrescriptionSubmit = (
    formValues: IFormValues,
    resetForm: (nextState?: Partial<FormikState<IFormValues>>) => void,
  ) => {
    const payload = preparePayload(formValues) as PrescriptionPayload;

    if (prescriptionsPassport || prescription) {
      const updatePayload = {
        prescription: payload.prescription
          ? {
              title: payload.prescription.title,
            }
          : undefined,
        medicationPassports: payload.medicationPassports?.map((mp, index) => ({
          ...mp,
          userId: undefined,
          prescriptionId: prescriptionsPassport?.id ?? prescription?.id,
          ...(editExistingMedication && index === 0
            ? { id: editMedicationPassportId }
            : {}),
        })),
      };

      void updatePrescriptionPassport({
        id: prescriptionsPassport?.id ?? prescription?.id ?? "",
        requestBody: updatePayload,
      });
    } else {
      void createPrescriptionPassport({ requestBody: payload });
    }
    resetForm(
      getResetFormObject(formValues) as unknown as Partial<
        FormikState<IFormValues>
      >,
    );
  };

  const { values, touched, setFieldValue, handleSubmit, resetForm, errors } =
    useFormik({
      initialValues: {
        title: prescription?.title ?? "",
        medicationId: editExistingMedication
          ? medicationToEdit?.medicationId
          : "",
        source: editExistingMedication ? medicationToEdit?.source : "",
        type: editExistingMedication ? medicationToEdit?.type : "",
        frequency: editExistingMedication ? medicationToEdit?.frequency : "",
        dosagesUnit: dosageUnitOptions[0].value,
        dosage: editExistingMedication ? medicationToEdit?.dosage : "",
        startDate: editExistingMedication
          ? moment(medicationToEdit?.startDate).toDate()
          : new Date().toISOString(),
        duration: editExistingMedication
          ? medicationToEdit?.durationInDays
          : "",
      },
      validationSchema: Yup.object({
        title: Yup.string(),
        medicationId: Yup.string().required("This field is required"),
        dosagesUnit: Yup.string().required("Dosage unit is required"),
        dosage: Yup.number().required("Dosage amount is required"),
        frequency: Yup.string().required("Dosage frequency is required"),
        source: Yup.string().required("This field is required"),
        startDate: Yup.date().required(
          "A start date before current time is required",
        ),
        duration: Yup.number(),
      }),
      onSubmit: (data): void => {
        onAddPrescriptionSubmit(data as IFormValues, resetForm);
      },
    });

  const medicationOptions =
    medications?.map((singleMedication) => ({
      label: singleMedication.name,
      value: singleMedication.id ?? "",
    })) ?? [];

  const frequenciesOptions = frequencies.map((item) => ({
    label: item.name,
    value: item.name ?? "",
  }));

  const handleClose = () => {
    onClose();
    resetForm();
  };

  return (
    <Modal
      confirmButtonLabel={editExistingMedication ? "Update" : "Save"}
      confirmButtonType="submit"
      confirmLoading={loading}
      footerComponent={
        addSaveAndAdd && (
          <div className="form-control flex justify-start">
            <Checkbox
              checked={saveAndAdd}
              label="Save and add new medication"
              onChange={() => void setSaveAndAdd(!saveAndAdd)}
            />
          </div>
        )
      }
      handleCancel={handleClose}
      handleConfirm={() => {
        handleSubmit();
      }}
      headerComponent={
        <div className="flex items-center gap-3">
          <div className="h-max w-max rounded-full border-[0.8px] border-gray-300 p-2 text-orange">
            <Icon name="pill" size={24} />
          </div>
          <span className="text-xl font-semibold">
            {editExistingMedication ? "Edit" : "Add"} Prescription
          </span>
        </div>
      }
      isModalOpen={isOpen}
      modalBoxClassName="w-full min-h-[50vh] h-max overflow-hidden bg-white"
      modalId={modalId}
      onDismiss={() => {
        handleClose();
        unmountDialogue();
      }}
    >
      <div className="px-4">
        <form className="grid gap-5">
          <div className="mx-auto w-full rounded-xl border p-3">
            <TextInput
              label="What is the prescription name?"
              name="title"
              onChange={(e) => {
                void setFieldValue("title", e?.target?.value);
              }}
              placeholder="Enter prescription name"
              value={values?.title}
            />
          </div>
          <div className="mx-auto w-full rounded-xl border p-3">
            <CustomSelect
              error={touched.medicationId ? errors.medicationId : ""}
              label="What is the medication name?"
              name="medicationId"
              onChange={(e) => void setFieldValue("medicationId", e?.value)}
              options={medicationOptions}
              placeholder="Select medication"
              value={
                medicationOptions?.find(
                  (item) => item.value === values?.medicationId,
                ) ?? null
              }
            />
            <div className="form-control flex justify-start">
              <Checkbox
                checked={values?.type === "past"}
                label="is a previous medication?"
                onChange={() => {
                  void setFieldValue(
                    "type",
                    values?.type === "past" ? "present" : "past",
                  );
                }}
                value={values?.type ?? "present"}
              />
            </div>
          </div>
          <div>
            <CustomSelect
              error={touched.frequency ? errors.frequency : ""}
              label="What is the frequency?"
              name="frequency"
              onChange={(e) => {
                void setFieldValue("frequency", e?.value);
              }}
              options={frequenciesOptions}
              placeholder="e.g Twice a day for 30 days"
              value={
                frequenciesOptions?.find(
                  (item) => item.value === values?.frequency?.trim(),
                ) ?? null
              }
            />
          </div>
          <div>
            <InputSelect
              error={
                touched.dosagesUnit || touched.dosage
                  ? (errors.dosagesUnit ?? errors.dosage)
                  : ""
              }
              inputClassName="px-3"
              inputProps={{ min: 0 } as HTMLAttributes<HTMLInputElement>}
              label="What is the dosage?"
              onChange={(e) => {
                void setFieldValue("dosage", e?.target?.value);
              }}
              onSelect={(e) => {
                void setFieldValue("dosagesUnit", e);
              }}
              options={dosageUnitOptions}
              placeholder="0"
              required
              selectedValue={
                dosageUnitOptions.find(
                  (item) => item.value === values?.dosagesUnit?.trim(),
                )?.value ?? ""
              }
              type="number"
              value={values.dosage}
            />
          </div>
          <div>
            <DateTimePicker
              className="w-full"
              error={touched.startDate ? errors.startDate : ""}
              label="Start Date / Time"
              maxDate={new Date()}
              onChange={(e) => void setFieldValue("startDate", e)}
              required
              value={new Date(values?.startDate)}
            />
          </div>
          <div>
            <NumberInput
              error={touched.duration ? errors.duration : ""}
              label="What is the medication duration in days?"
              min={0}
              name="duration"
              onChange={(e) => void setFieldValue("duration", e?.target?.value)}
              placeholder="Select duration"
              value={values?.duration}
            />
          </div>
          <div className="pb-14">
            <CustomSelect
              error={touched.source ? errors.source : ""}
              label="Medication prescribed by"
              name="source"
              onChange={(e) => {
                void setFieldValue("source", e?.value);
              }}
              options={sourceOptionsPrescriptions}
              placeholder="Select where"
              value={
                sourceOptionsPrescriptions?.find(
                  (item) => item.value === values?.source?.trim(),
                ) ?? null
              }
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddMedication;
